import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, UserIcon, ActivityIcon, RulerIcon, DropletIcon, FlameIcon, ScaleIcon, HeartIcon, AlertCircleIcon, CheckCircleIcon, TrendingUpIcon, InfoIcon, RefreshCcwIcon, SparklesIcon, Loader2Icon, CheckIcon, MinusIcon, PlusIcon } from 'lucide-react';
interface BMIData {
  gender: 'male' | 'female' | '';
  activity: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active' | '';
  age: string;
  height: string;
  weight: string;
  waist: string;
  hip: string;
}
interface BMIResults {
  bmi: number;
  classification: string;
  bmr: number;
  tdee: number;
  waterIntake: number;
  idealWeightRange: {
    lower: number;
    upper: number;
  };
  calorieTargets: {
    mildLoss: number;
    standardLoss: number;
    gain: number;
  };
  whr?: number;
  whrRisk?: string;
}
type Step = 'gender' | 'activity' | 'measurements' | 'calculating' | 'results';
const healthTips = ['Regular physical activity helps maintain a healthy weight and reduces disease risk.', 'A balanced diet with fruits, vegetables, and whole grains supports overall health.', 'Staying hydrated is essential for metabolism and body function.', 'Quality sleep (7-9 hours) is crucial for weight management and recovery.', 'Stress management through meditation or exercise improves overall wellbeing.', 'Small, consistent lifestyle changes lead to sustainable long-term results.'];
export function BMICalculatorPage() {
  const [step, setStep] = useState<Step>('gender');
  const [progress, setProgress] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [data, setData] = useState<BMIData>({
    gender: '',
    activity: '',
    age: '',
    height: '',
    weight: '',
    waist: '',
    hip: ''
  });
  const [results, setResults] = useState<BMIResults | null>(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const activityFactors = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9
  };
  const activityOptions = [{
    id: 'sedentary',
    label: 'Sedentary',
    description: 'Little to no exercise',
    icon: '🛋️'
  }, {
    id: 'light',
    label: 'Light',
    description: 'Exercise 1-3 days/week',
    icon: '🚶'
  }, {
    id: 'moderate',
    label: 'Moderate',
    description: 'Exercise 3-5 days/week',
    icon: '🏃'
  }, {
    id: 'active',
    label: 'Active',
    description: 'Exercise 6-7 days/week',
    icon: '💪'
  }, {
    id: 'very_active',
    label: 'Very Active',
    description: 'Intense exercise daily',
    icon: '🏋️'
  }];
  // Rotate health tips during calculation
  useEffect(() => {
    if (step === 'calculating') {
      const interval = setInterval(() => {
        setCurrentTipIndex(prev => (prev + 1) % healthTips.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [step]);
  // Calculating animation - enhanced with smoother progression
  useEffect(() => {
    if (step === 'calculating') {
      setProgress(0);
      setCurrentTipIndex(0);
      const duration = 4000; // 4 seconds for a smooth experience
      const interval = 30;
      const increment = interval / duration * 100;
      const timer = setInterval(() => {
        setProgress(prev => {
          const next = prev + increment;
          if (next >= 100) {
            clearInterval(timer);
            setTimeout(() => {
              const calculatedResults = calculateResults();
              setResults(calculatedResults);
              setStep('results');
            }, 200);
            return 100;
          }
          return next;
        });
      }, interval);
      return () => clearInterval(timer);
    }
  }, [step]);
  const calculateResults = (): BMIResults => {
    const heightM = parseFloat(data.height) / 100;
    const weightKg = parseFloat(data.weight);
    const ageYears = parseInt(data.age);
    // BMI
    const bmi = weightKg / (heightM * heightM);
    // Classification
    let classification = '';
    if (bmi < 18.5) classification = 'Underweight';else if (bmi < 25) classification = 'Normal';else if (bmi < 30) classification = 'Overweight';else if (bmi < 35) classification = 'Obesity I';else if (bmi < 40) classification = 'Obesity II';else classification = 'Obesity III';
    // BMR (Mifflin-St Jeor)
    const bmr = data.gender === 'male' ? 10 * weightKg + 6.25 * parseFloat(data.height) - 5 * ageYears + 5 : 10 * weightKg + 6.25 * parseFloat(data.height) - 5 * ageYears - 161;
    // TDEE
    const tdee = bmr * activityFactors[data.activity as keyof typeof activityFactors];
    // Water intake
    const waterIntake = Math.max(2.0, Math.round(weightKg * 0.033 * 10) / 10);
    // Ideal weight range
    const idealWeightRange = {
      lower: Math.round(18.5 * heightM * heightM * 10) / 10,
      upper: Math.round(24.9 * heightM * heightM * 10) / 10
    };
    // Calorie targets
    const calorieTargets = {
      mildLoss: Math.round(tdee - 300),
      standardLoss: Math.round(tdee - 500),
      gain: Math.round(tdee + 300)
    };
    // WHR if provided
    let whr: number | undefined;
    let whrRisk: string | undefined;
    if (data.waist && data.hip) {
      whr = Math.round(parseFloat(data.waist) / parseFloat(data.hip) * 100) / 100;
      const threshold = data.gender === 'male' ? 0.9 : 0.85;
      whrRisk = whr > threshold ? 'High Risk' : 'Low Risk';
    }
    return {
      bmi: Math.round(bmi * 100) / 100,
      classification,
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      waterIntake,
      idealWeightRange,
      calorieTargets,
      whr,
      whrRisk
    };
  };
  const handleGenderSelect = (gender: 'male' | 'female') => {
    setData({
      ...data,
      gender
    });
    setTimeout(() => {
      setStep('activity');
    }, 300);
  };
  const handleActivitySelect = (activity: string) => {
    setData({
      ...data,
      activity: activity as any
    });
    setTimeout(() => {
      setStep('measurements');
    }, 300);
  };
  const handleCalculate = () => {
    setStep('calculating');
  };
  const handleBack = () => {
    if (step === 'activity') setStep('gender');else if (step === 'measurements') setStep('activity');
  };
  const handleReset = () => {
    setStep('gender');
    setData({
      gender: '',
      activity: '',
      age: '',
      height: '',
      weight: '',
      waist: '',
      hip: ''
    });
    setResults(null);
    setProgress(0);
  };
  const getStepNumber = () => {
    if (step === 'gender') return 1;
    if (step === 'activity') return 2;
    if (step === 'measurements') return 3;
    return 3;
  };
  const isFormValid = () => {
    return data.age !== '' && data.height !== '' && data.weight !== '' && parseInt(data.age) >= 14 && parseInt(data.age) <= 80 && parseFloat(data.height) >= 120 && parseFloat(data.height) <= 220 && parseFloat(data.weight) >= 35 && parseFloat(data.weight) <= 250;
  };
  const getClassificationColor = (classification: string) => {
    if (classification === 'Underweight') return 'text-blue-600';
    if (classification === 'Normal') return 'text-green-600';
    if (classification === 'Overweight') return 'text-yellow-600';
    if (classification.includes('Obesity')) return 'text-red-600';
    return 'text-gray-600';
  };
  const getClassificationBg = (classification: string) => {
    if (classification === 'Underweight') return 'bg-blue-50 border-blue-200';
    if (classification === 'Normal') return 'bg-green-50 border-green-200';
    if (classification === 'Overweight') return 'bg-yellow-50 border-yellow-200';
    if (classification.includes('Obesity')) return 'bg-red-50 border-red-200';
    return 'bg-gray-50 border-gray-200';
  };
  const NumericInput = ({
    label,
    value,
    onChange,
    placeholder,
    min,
    max,
    step = 1,
    required = false
  }: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    min?: number;
    max?: number;
    step?: number;
    required?: boolean;
  }) => {
    const increment = () => {
      const current = parseFloat(value) || 0;
      const newValue = Math.min(max || Infinity, current + step);
      onChange(newValue.toString());
    };
    const decrement = () => {
      const current = parseFloat(value) || 0;
      const newValue = Math.max(min || 0, current - step);
      onChange(newValue.toString());
    };
    // Prevent buttons from stealing focus and stop event propagation
    const handleButtonClick = (action: () => void) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      action();
    };
    return <div data-id="element-5835" data-mp="2q6ujyfoazs9n">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5836" data-mp="2q6kquj7yp4zq">
          {label} {required && <span className="text-red-500" data-id="element-5837" data-mp="2l7zr0kwmgnbt">*</span>}
        </label>
        <div className="flex items-center gap-2" data-id="element-5838" data-mp="2l5ndcjxwcz4k">
          <button type="button" onClick={handleButtonClick(decrement)} onMouseDown={e => e.preventDefault()} className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0" tabIndex={-1} data-id="element-5839" data-mp="3indn498kg7rg">
            <MinusIcon className="w-5 h-5 text-gray-700" data-id="element-5840" data-mp="3bijvnj01wv6" />
          </button>
          <input type="number" min={min} max={max} step={step} value={value} onChange={e => onChange(e.target.value)} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] text-center font-semibold" placeholder={placeholder} inputMode="decimal" data-id="element-5841" data-mp="ha8ij7lgw4ln" />
          <button type="button" onClick={handleButtonClick(increment)} onMouseDown={e => e.preventDefault()} className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0" tabIndex={-1} data-id="element-5842" data-mp="10xg1zjtgw06l">
            <PlusIcon className="w-5 h-5 text-gray-700" data-id="element-5843" data-mp="14xq3aj6be2f4" />
          </button>
        </div>
      </div>;
  };
  return <HelmetProvider data-id="element-5844" data-mp="1tk7cqp1iu8j9">
      <SEO title="BMI Calculator" description="Calculate your Body Mass Index (BMI) and get personalized health insights. Free BMI calculator with recommendations for a healthier lifestyle in Saudi Arabia." canonical="/en/bmi-calculator/" robots="index, follow, max-image-preview:large" hreflangAr="/bmi-calculator/" hreflangEn="/en/bmi-calculator/" hreflangDefault="/bmi-calculator/" ogImage="https://cdn.innovaksa.net/og/bmi-calculator.jpg" keywords="BMI calculator, body mass index, health calculator, weight calculator, BMR calculator, TDEE calculator, health metrics, wellness calculator, fitness calculator Saudi Arabia" data-id="element-5845" data-mp="1tkphka291v2s" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-5846" data-mp="mx22ytj1m2mq">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-5847" data-mp="rvder9swen7l">
          <AnnouncementBar data-id="element-5848" data-mp="rx6iohc4mtbw" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-5849" data-mp="2tjzowaqdokpl" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-5850" data-mp="2os86inhdllaj" />
        </div>
        <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-5851" data-mp="3cjnccq8d2kqj">
          <div className="fixed inset-x-0 top-0 z-50 md:hidden" data-id="element-5852" data-mp="37k0ycekpl0go">
            <MobileInternalHeader title="BMI Calculator" data-id="element-5853" data-mp="37ktxouhc8nos" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'BMI Calculator'
        }]} title="BMI Calculator" description="Calculate your Body Mass Index and get personalized health recommendations" data-id="element-5854" data-mp="33kjsvl5a83bi" />
          <div className="container mx-auto px-4 max-w-5xl py-8" data-id="element-5855" data-mp="2nznb3z3uylm">
            {/* Progress Bar */}
            {step !== 'results' && step !== 'calculating' && <div className="mb-8" data-id="element-5856" data-mp="bml4auoxi5ka">
                <div className="flex justify-between mb-2" data-id="element-5857" data-mp="bljmm7deqktl">
                  <span className="text-sm font-medium text-gray-700" data-id="element-5858" data-mp="gel84y84dim5">
                    Step {getStepNumber()} of 3
                  </span>
                  <span className="text-sm font-medium text-gray-700" data-id="element-5859" data-mp="leqqtowevus3">
                    {Math.round(getStepNumber() / 3 * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-5860" data-mp="vbbx0dk63bxi">
                  <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] h-2 rounded-full transition-all duration-300" style={{
                width: `${getStepNumber() / 3 * 100}%`
              }} data-id="element-5861" data-mp="zcp1okbix92u"></div>
                </div>
              </div>}
            {/* Step Content */}
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8" data-id="element-5862" data-mp="21saeprujbmqi">
              {/* Step 1: Gender */}
              {step === 'gender' && <div data-id="element-5863" data-mp="2fr9qw00v295s">
                  <div className="flex items-center mb-6" data-id="element-5864" data-mp="2fpp6v4c6a8m6">
                    <UserIcon className="w-6 h-6 text-[#1E2B55] mr-2" data-id="element-5865" data-mp="2atqez6rmy8h0" />
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-5866" data-mp="1n2unybv07hat">
                      Select Your Gender
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5867" data-mp="1i5ugpxmf48i4">
                    <button className="p-8 rounded-xl border-2 border-gray-200 hover:border-[#1E2B55] hover:bg-blue-50 transition-all transform hover:scale-105" onClick={() => handleGenderSelect('male')} data-id="element-5868" data-mp="1wtpcjmvq2szf">
                      <div className="text-5xl mb-3" data-id="element-5869" data-mp="ze1s0fedt9g7">👨</div>
                      <div className="font-bold text-xl" data-id="element-5870" data-mp="zbfhc6w72dtr">Male</div>
                    </button>
                    <button className="p-8 rounded-xl border-2 border-gray-200 hover:border-[#1E2B55] hover:bg-blue-50 transition-all transform hover:scale-105" onClick={() => handleGenderSelect('female')} data-id="element-5871" data-mp="3aysjqoj3yjit">
                      <div className="text-5xl mb-3" data-id="element-5872" data-mp="362lj9b9he089">👩</div>
                      <div className="font-bold text-xl" data-id="element-5873" data-mp="3tqv347n4wdwr">Female</div>
                    </button>
                  </div>
                </div>}
              {/* Step 2: Activity Level */}
              {step === 'activity' && <div data-id="element-5874" data-mp="3kq6mr6a9q1gp">
                  <div className="flex items-center mb-6" data-id="element-5875" data-mp="3kssy7h0a5147">
                    <ActivityIcon className="w-6 h-6 text-[#1E2B55] mr-2" data-id="element-5876" data-mp="3pogjvmfjzc4y" />
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-5877" data-mp="29b05e25kgt0h">
                      Activity Level
                    </h2>
                  </div>
                  <div className="space-y-3" data-id="element-5878" data-mp="2e8jhowipevps">
                    {activityOptions.map(option => <button key={option.id} className="w-full p-5 rounded-xl border-2 border-gray-200 hover:border-[#1E2B55] hover:bg-blue-50 text-left transition-all flex items-center transform hover:scale-102" onClick={() => handleActivitySelect(option.id)} data-id="element-5879" data-mp="jwii8mb8k1jh">
                        <div className="text-4xl mr-4" data-id="element-5880" data-mp="14y2ikw7bnnh">{option.icon}</div>
                        <div className="flex-1" data-id="element-5881" data-mp="3s9ocj1e3vnlp">
                          <div className="font-bold text-lg" data-id="element-5882" data-mp="3s8wg5ocoplud">
                            {option.label}
                          </div>
                          <div className="text-sm text-gray-600" data-id="element-5883" data-mp="3j9stybi80qbn">
                            {option.description}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </div>}
              {/* Step 3: Measurements */}
              {step === 'measurements' && <div data-id="element-5884" data-mp="2zdos3wqyekdr">
                  <div className="flex items-center mb-6" data-id="element-5885" data-mp="34emb2bjsfigb">
                    <RulerIcon className="w-6 h-6 text-[#1E2B55] mr-2" data-id="element-5886" data-mp="34d1i2qd4g7kk" />
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-5887" data-mp="396nmjv94t55a">
                      Your Measurements
                    </h2>
                  </div>
                  <form onSubmit={e => {
                e.preventDefault();
                if (isFormValid()) {
                  handleCalculate();
                }
              }} data-id="element-5888" data-mp="2ggx5peg47p5h">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-5889" data-mp="2pg22ogc13afj">
                      <div data-id="element-5890" data-mp="2uhrh85ygm68d">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5891" data-mp="2uhzonibtcjc6">
                          Age (years) <span className="text-red-500" data-id="element-5892" data-mp="4tgzflr4dmnj">*</span>
                        </label>
                        <div className="relative" data-id="element-5893" data-mp="9omm4mscj5co">
                          <input type="number" min="14" max="80" value={data.age} onChange={e => setData({
                        ...data,
                        age: e.target.value
                      })} className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent text-center font-semibold text-lg transition-all ${data.age && parseInt(data.age) >= 14 && parseInt(data.age) <= 80 ? 'border-green-300 bg-green-50' : 'border-gray-300'}`} placeholder="25" required data-id="element-5894" data-mp="3hii4fqoxn1tn" />
                          {data.age && parseInt(data.age) >= 14 && parseInt(data.age) <= 80 && <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" data-id="element-5895" data-mp="20rfz2p63kfai" />}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center" data-id="element-5896" data-mp="1r1wb553j1v8s">
                          14-80 years
                        </p>
                      </div>
                      <div data-id="element-5897" data-mp="1h36vliz3smvp">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5898" data-mp="1h1vqnftx9930">
                          Height (cm) <span className="text-red-500" data-id="element-5899" data-mp="1m32xnco8q48m">*</span>
                        </label>
                        <div className="relative" data-id="element-5900" data-mp="183adk077ym9m">
                          <input type="number" min="120" max="220" step="0.1" value={data.height} onChange={e => setData({
                        ...data,
                        height: e.target.value
                      })} className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent text-center font-semibold text-lg transition-all ${data.height && parseFloat(data.height) >= 120 && parseFloat(data.height) <= 220 ? 'border-green-300 bg-green-50' : 'border-gray-300'}`} placeholder="170" required data-id="element-5901" data-mp="184lhc00nzu0q" />
                          {data.height && parseFloat(data.height) >= 120 && parseFloat(data.height) <= 220 && <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" data-id="element-5902" data-mp="39a6x8e1g2uo2" />}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center" data-id="element-5903" data-mp="3d9o1x591rns6">
                          120-220 cm
                        </p>
                      </div>
                      <div data-id="element-5904" data-mp="30sd92pz7dnr4">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5905" data-mp="30v99qm2fqqj9">
                          Weight (kg) <span className="text-red-500" data-id="element-5906" data-mp="3focpqp0dow7v">*</span>
                        </label>
                        <div className="relative" data-id="element-5907" data-mp="3ap9l35ob8s0f">
                          <input type="number" min="35" max="250" step="0.1" value={data.weight} onChange={e => setData({
                        ...data,
                        weight: e.target.value
                      })} className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent text-center font-semibold text-lg transition-all ${data.weight && parseFloat(data.weight) >= 35 && parseFloat(data.weight) <= 250 ? 'border-green-300 bg-green-50' : 'border-gray-300'}`} placeholder="70" required data-id="element-5908" data-mp="2n0yt6s0le2oa" />
                          {data.weight && parseFloat(data.weight) >= 35 && parseFloat(data.weight) <= 250 && <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" data-id="element-5909" data-mp="2a43n4d7xlhks" />}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center" data-id="element-5910" data-mp="2k0yhql132xts">
                          35-250 kg
                        </p>
                      </div>
                    </div>
                    {/* Submit Button */}
                    <div className="flex justify-center mb-6" data-id="element-5911" data-mp="3vf3otmwlfntc">
                      <button type="submit" disabled={!isFormValid()} className={`px-8 py-4 rounded-xl font-bold text-lg transition-all transform ${isFormValid() ? 'bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] text-white hover:shadow-xl hover:scale-105' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-5912" data-mp="1jtl327dx4o2k">
                        {isFormValid() ? <span className="flex items-center" data-id="element-5913" data-mp="113va7ptpucda">
                            <SparklesIcon className="w-5 h-5 mr-2" data-id="element-5914" data-mp="w37j745hlvj2" />
                            Calculate My BMI
                          </span> : 'Complete All Fields'}
                      </button>
                    </div>
                    <div className={`rounded-lg p-4 flex items-start transition-all ${isFormValid() ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`} data-id="element-5915" data-mp="2lhce1pscar3l">
                      {isFormValid() ? <>
                          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-5916" data-mp="2tjcxblv5s2k9" />
                          <p className="text-sm text-gray-700" data-id="element-5917" data-mp="2eksrnyesa8n6">
                            All fields are complete! Click the button above to
                            calculate your results.
                          </p>
                        </> : <>
                          <InfoIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-5918" data-mp="2al0ok185sup7" />
                          <p className="text-sm text-gray-700" data-id="element-5919" data-mp="1vwnpgvdryvqh">
                            Please fill in all required fields with valid values
                            to calculate your BMI
                          </p>
                        </>}
                    </div>
                  </form>
                </div>}
              {/* Calculating State - Enhanced */}
              {step === 'calculating' && <div className="text-center py-12" data-id="element-5920" data-mp="1394icd4aulmh">
                  <div className="inline-flex items-center justify-center mb-6" data-id="element-5921" data-mp="13afnag9hdzf6">
                    <Loader2Icon className="w-16 h-16 text-[#1E2B55] animate-spin" data-id="element-5922" data-mp="1h9oz73qnm7dc" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5923" data-mp="1c8rnna05gbkt">
                    Calculating Your BMI Results
                  </h2>
                  <p className="text-gray-600 mb-8" data-id="element-5924" data-mp="1m7qkzwfgsksi">
                    Analyzing your measurements and activity level...
                  </p>
                  {/* Enhanced Progress Bar */}
                  <div className="max-w-md mx-auto mb-6" data-id="element-5925" data-mp="200ybfkrt7a8m">
                    <div className="flex justify-between mb-2" data-id="element-5926" data-mp="1vytk4sou2pp5">
                      <span className="text-sm font-medium text-gray-700" data-id="element-5927" data-mp="29wrwxtniae2j">
                        Progress
                      </span>
                      <span className="text-sm font-medium text-[#1E2B55]" data-id="element-5928" data-mp="24zrr9jukjdby">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner" data-id="element-5929" data-mp="16lm4vrig8mqb">
                      <div className="h-full bg-gradient-to-r from-[#1E2B55] via-purple-600 to-[#1E2B55] rounded-full transition-all duration-300 ease-out relative" style={{
                    width: `${progress}%`
                  }} data-id="element-5930" data-mp="1bkfl3rp4eru6">
                        <div className="absolute inset-0 bg-white opacity-20 animate-pulse" data-id="element-5931" data-mp="1q8tpw863kqdd"></div>
                      </div>
                    </div>
                  </div>
                  {/* Health Tips */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto" data-id="element-5932" data-mp="1z8yuybntjcod">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center justify-center" data-id="element-5933" data-mp="1ubyx8cnxz">
                      <SparklesIcon className="w-5 h-5 text-blue-600 mr-2" data-id="element-5934" data-mp="4z4oupfl3brp" />
                      Health Tip
                    </h3>
                    <div className="min-h-[60px]" data-id="element-5935" data-mp="3orop4p64hy4v">
                      <p className="text-gray-700 text-sm leading-relaxed" data-id="element-5936" data-mp="3oqvs4fwyumzx">
                        💡 {healthTips[currentTipIndex]}
                      </p>
                    </div>
                  </div>
                </div>}
              {/* Results */}
              {step === 'results' && results && <div data-id="element-5937" data-mp="bdaix4u61ldm">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-5938" data-mp="bcsf9n56c4vo">
                    Your Health Metrics
                  </h2>
                  {/* Results Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8" data-id="element-5939" data-mp="2cvuvxpths6f">
                    {/* BMI Card */}
                    <div className="rounded-2xl border-2 border-gray-200 bg-white shadow-sm p-6 md:p-8 min-h-[260px] flex flex-col" data-id="element-5940" data-mp="2r5hizau67xqe">
                      <div className="flex items-center justify-between mb-4" data-id="element-5941" data-mp="2n4xokf0zf87y">
                        <h3 className="font-bold text-lg text-gray-900" data-id="element-5942" data-mp="q0nhoivkr9tv">BMI</h3>
                        <div className="relative group" data-id="element-5943" data-mp="v2ctw1uj9tjj">
                          <InfoIcon className="w-5 h-5 text-gray-400 cursor-help" data-id="element-5944" data-mp="h1ijxeru7kir" />
                          <div className="absolute bottom-full right-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-5945" data-mp="h3lm0x69l43u">
                            Body Mass Index: A measure of body fat based on
                            height and weight
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center" data-id="element-5946" data-mp="14zhi077xvz2z">
                        <div className="relative w-32 h-32 mb-4" data-id="element-5947" data-mp="14x4ttcbl8shh">
                          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-5948" data-mp="1snh9xagzlvj5">
                            <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" data-id="element-5949" data-mp="26tiydczyx4rc" />
                            <circle cx="50" cy="50" r="40" fill="none" stroke={results.classification === 'Underweight' ? '#3b82f6' : results.classification === 'Normal' ? '#10b981' : results.classification === 'Overweight' ? '#f59e0b' : '#ef4444'} strokeWidth="8" strokeDasharray={`${results.bmi / 40 * 251.2} 251.2`} strokeLinecap="round" data-id="element-5950" data-mp="2blu6320y9uug" />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-5951" data-mp="o6oq98fnfmco">
                            <span className="text-3xl font-bold text-gray-900" data-id="element-5952" data-mp="j7u8m9tf43u5">
                              {results.bmi}
                            </span>
                          </div>
                        </div>
                        <div className={`px-4 py-2 rounded-full border-2 font-semibold ${getClassificationBg(results.classification)} ${getClassificationColor(results.classification)}`} data-id="element-5953" data-mp="1736peawtuoiy">
                          {results.classification}
                        </div>
                      </div>
                    </div>
                    {/* Body Type Card */}
                    <div className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-purple-50 to-purple-100 shadow-sm p-6 md:p-8 min-h-[260px] flex flex-col" data-id="element-5954" data-mp="224p7jpbp4659">
                      <div className="flex items-center justify-between mb-4" data-id="element-5955" data-mp="2c2cys5a3evio">
                        <h3 className="font-bold text-lg text-gray-900" data-id="element-5956" data-mp="2c4fzbj8vg71n">
                          Body Type
                        </h3>
                        <div className="relative group" data-id="element-5957" data-mp="2l3jisobyaogo">
                          <InfoIcon className="w-5 h-5 text-gray-400 cursor-help" data-id="element-5958" data-mp="2l0xd6u8mej13" />
                          <div className="absolute bottom-full right-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-5959" data-mp="2q0jqt4sd21af">
                            Classification based on your BMI score
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center" data-id="element-5960" data-mp="38s2ocw3m8n52">
                        <div className="text-6xl mb-4" data-id="element-5961" data-mp="18puuze4ta4ld">
                          {results.classification === 'Underweight' ? '🧍' : results.classification === 'Normal' ? '🚶' : results.classification === 'Overweight' ? '🚶‍♂️' : '🧍‍♂️'}
                        </div>
                        <p className="text-center font-semibold text-purple-900 text-lg" data-id="element-5962" data-mp="1scir1u4e6jkw">
                          {results.classification}
                        </p>
                        <p className="text-center text-sm text-purple-700 mt-2" data-id="element-5963" data-mp="21dz2m56y0rqp">
                          {results.classification === 'Underweight' ? 'Focus on gaining healthy weight' : results.classification === 'Normal' ? 'Maintain your healthy lifestyle' : results.classification === 'Overweight' ? 'Consider a balanced diet plan' : 'Consult with a healthcare professional'}
                        </p>
                      </div>
                    </div>
                    {/* BMR Card */}
                    <div className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-orange-50 to-orange-100 shadow-sm p-6 md:p-8 min-h-[260px] flex flex-col" data-id="element-5964" data-mp="28yb3a7goouw7">
                      <div className="flex items-center justify-between mb-4" data-id="element-5965" data-mp="1v14m2hblz493">
                        <h3 className="font-bold text-lg text-gray-900" data-id="element-5966" data-mp="1zz64r154h1k1">BMR</h3>
                        <div className="relative group" data-id="element-5967" data-mp="1lzeunbhq85bb">
                          <InfoIcon className="w-5 h-5 text-gray-400 cursor-help" data-id="element-5968" data-mp="1pzf92w4tsgid" />
                          <div className="absolute bottom-full right-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-5969" data-mp="1q1i6i18b5bx4">
                            Basal Metabolic Rate: Calories burned at rest
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center" data-id="element-5970" data-mp="2qoig1he2j246">
                        <FlameIcon className="w-16 h-16 text-orange-500 mb-4" data-id="element-5971" data-mp="2lnky95wz0a37" />
                        <p className="text-4xl font-bold text-orange-600 mb-2" data-id="element-5972" data-mp="2gqkwv8b2fvif">
                          {results.bmr}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-5973" data-mp="2cqapbr7mkx1g">
                          kcal/day at rest
                        </p>
                      </div>
                    </div>
                    {/* Water Card */}
                    <div className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-blue-50 to-blue-100 shadow-sm p-6 md:p-8 min-h-[260px] flex flex-col" data-id="element-5974" data-mp="1xzjwcfuarrwr">
                      <div className="flex items-center justify-between mb-4" data-id="element-5975" data-mp="1t2bcmati3fte">
                        <h3 className="font-bold text-lg text-gray-900" data-id="element-5976" data-mp="1oz5anfdf8asq">
                          Water Intake
                        </h3>
                        <div className="relative group" data-id="element-5977" data-mp="8i66kyd6bnkz">
                          <InfoIcon className="w-5 h-5 text-gray-400 cursor-help" data-id="element-5978" data-mp="8glc18qv04n4" />
                          <div className="absolute bottom-full right-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-5979" data-mp="diangphzukbq">
                            Recommended daily water intake based on your weight
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center" data-id="element-5980" data-mp="3qvnj06gukcls">
                        <DropletIcon className="w-16 h-16 text-blue-500 mb-4" data-id="element-5981" data-mp="3vmm4rlklu2wf" />
                        <p className="text-4xl font-bold text-blue-600 mb-2" data-id="element-5982" data-mp="3vop5azjdvefe">
                          {results.waterIntake}L
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-5983" data-mp="335a1h9vw1w5g">per day</p>
                      </div>
                    </div>
                    {/* TEE Card */}
                    <div className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-green-50 to-green-100 shadow-sm p-6 md:p-8 min-h-[260px] flex flex-col" data-id="element-5984" data-mp="2r4jsclquhao9">
                      <div className="flex items-center justify-between mb-4" data-id="element-5985" data-mp="34xsxubbo9c1i">
                        <h3 className="font-bold text-lg text-gray-900" data-id="element-5986" data-mp="2zx59xyiqpbbi">TEE</h3>
                        <div className="relative group" data-id="element-5987" data-mp="2c82i5xue623a">
                          <InfoIcon className="w-5 h-5 text-gray-400 cursor-help" data-id="element-5988" data-mp="285fp5tlzib4i" />
                          <div className="absolute bottom-full right-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-5989" data-mp="287se4qq5tlr2">
                            Total Energy Expenditure: Daily calories burned
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center" data-id="element-5990" data-mp="3skrcr4k58bxb">
                        <TrendingUpIcon className="w-16 h-16 text-green-500 mb-4" data-id="element-5991" data-mp="1ecqioumm0w3b" />
                        <p className="text-4xl font-bold text-green-600 mb-2" data-id="element-5992" data-mp="1jcuwue01peqd">
                          {results.tdee}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-5993" data-mp="15ewjnbxgnocg">kcal/day total</p>
                      </div>
                    </div>
                    {/* Calorie Card */}
                    <div className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-indigo-50 to-indigo-100 shadow-sm p-6 md:p-8 min-h-[260px] flex flex-col" data-id="element-5994" data-mp="1y20wu46pj8zb">
                      <div className="flex items-center justify-between mb-4" data-id="element-5995" data-mp="1ochb8qrm110r">
                        <h3 className="font-bold text-lg text-gray-900" data-id="element-5996" data-mp="1taix1jgf7efx">
                          Maintenance
                        </h3>
                        <div className="relative group" data-id="element-5997" data-mp="2gzlccknl0v79">
                          <InfoIcon className="w-5 h-5 text-gray-400 cursor-help" data-id="element-5998" data-mp="2lqtlrgowwgkf" />
                          <div className="absolute bottom-full right-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-5999" data-mp="lou0ldbacf5l">
                            Daily calories needed to maintain current weight
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center" data-id="element-6000" data-mp="71r499yp8cju">
                        <HeartIcon className="w-16 h-16 text-indigo-500 mb-4" data-id="element-6001" data-mp="14n6ch6tl034v" />
                        <p className="text-4xl font-bold text-indigo-600 mb-2" data-id="element-6002" data-mp="zpmzsbcj7yf1">
                          {results.tdee}
                        </p>
                        <p className="text-sm text-gray-600 text-center" data-id="element-6003" data-mp="urd3tyizgtsd">
                          kcal/day to maintain weight
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Disclaimer */}
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-6" data-id="element-6004" data-mp="3bcky4a5i5y98">
                    <div className="flex items-start" data-id="element-6005" data-mp="1qxogxoorefeg">
                      <AlertCircleIcon className="w-6 h-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-6006" data-mp="1uxfd67x21nqd" />
                      <div data-id="element-6007" data-mp="1zw9xjeao7kcl">
                        <h3 className="font-bold text-gray-900 mb-2" data-id="element-6008" data-mp="1zw057k25l13q">
                          Important Disclaimer
                        </h3>
                        <p className="text-sm text-gray-700" data-id="element-6009" data-mp="29mlcrwzt1fys">
                          This calculator is for educational purposes only and
                          is not a substitute for professional medical advice.
                          Always consult with a qualified healthcare
                          professional before making significant changes to your
                          diet or exercise routine.
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-6010" data-mp="t8v4ee1nk5jc">
                    <button onClick={handleReset} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-white text-[#1E2B55] border-2 border-[#1E2B55] font-semibold rounded-xl hover:bg-blue-50 transition-all" data-id="element-6011" data-mp="14xphmn0ljl3d">
                      <RefreshCcwIcon className="w-5 h-5 mr-2" data-id="element-6012" data-mp="1juzu9zoz6x0o" />
                      Recalculate
                    </button>
                    <Link to="/categories" className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-[#1E2B55] text-white font-bold rounded-xl hover:bg-blue-900 transition-all shadow-md" data-id="element-6013" data-mp="m5wpsvx9k5x8">
                      Browse Supplements
                      <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-6014" data-mp="3hhx847asjub" />
                    </Link>
                  </div>
                </div>}
              {/* Back Button */}
              {(step === 'activity' || step === 'measurements') && <div className="mt-6" data-id="element-6015" data-mp="2lptc2wxa8un8">
                  <button onClick={handleBack} className="flex items-center px-4 py-2 text-gray-600 hover:text-[#1E2B55] font-medium transition-colors" data-id="element-6016" data-mp="2hpaiaxbtxeie">
                    <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-6017" data-mp="27rcw0fdj5rsf" />
                    Back
                  </button>
                </div>}
            </div>
          </div>
        </main>
        <Footer data-id="element-6018" data-mp="1y0i8z8sgmo5x" />
        <MobileFooterNav data-id="element-6019" data-mp="1xzz0eiop9c4k" />
        <PharmacistChat data-id="element-6020" data-mp="1xzqniqsku6td" />
        <div className="h-16 md:hidden" data-id="element-6021" data-mp="1xz7393fgf6c6"></div>
      </div>
    </HelmetProvider>;
}